import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../utils/api.js';
import { useAuth } from '../context/AuthContext.jsx';

const STATUS_LABELS = {
  open:          'Open',
  in_progress:   'In Progress',
  pending_staff: 'Pending Staff',
  pending_user:  'Pending User',
  resolved:      'Resolved',
  closed:        'Closed',
};

const CLOSED_STATUSES = ['resolved', 'closed'];
const API_BASE = import.meta.env.VITE_API_URL || '/api';

function TicketRow({ ticket }) {
  const isClosed = CLOSED_STATUSES.includes(ticket.status);
  const content = (
    <>
      <div style={{ fontSize: 20, flexShrink: 0 }}>{ticket.category_emoji}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text3)', fontSize: 12, fontWeight: 600 }}>#{ticket.number}</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{ticket.category_label}</span>
          <span className={`badge badge-${ticket.status}`}>{STATUS_LABELS[ticket.status] || ticket.status}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          {ticket.created_by_username} · opened {formatDistanceToNow(new Date(ticket.opened_at), { addSuffix: true })}
          {ticket.closed_at && ` · closed ${formatDistanceToNow(new Date(ticket.closed_at), { addSuffix: true })}`}
        </div>
      </div>
      <div style={{ fontSize: 12, color: isClosed ? 'var(--accent)' : 'var(--text3)', flexShrink: 0 }}>
        {isClosed ? 'View transcript →' : 'Open in Discord'}
      </div>
    </>
  );

  const rowStyle = {
    display: 'flex', alignItems: 'center', gap: 14,
    background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 18px', marginBottom: 8,
    textDecoration: 'none', cursor: isClosed ? 'pointer' : 'default',
    opacity: isClosed ? 1 : 0.7,
  };

  if (isClosed) {
    return (
      <a href={`${API_BASE}/transcripts/${ticket.number}`} target="_blank" rel="noopener noreferrer" style={rowStyle}>
        {content}
      </a>
    );
  }
  return <div style={rowStyle}>{content}</div>;
}

export default function TicketsPage() {
  const { user } = useAuth();
  const isStaff  = user.role_level >= 1;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tickets?limit=100').then(res => {
      if (res?.ok) setTickets(res.data.tickets);
      setLoading(false);
    });
  }, []);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 2 }}>
          {isStaff ? 'Tickets' : 'My Tickets'}
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>
          Closed tickets link to their transcript. Open tickets are handled in Discord.
        </p>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text2)' }}>Loading…</div>
      ) : tickets.length === 0 ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 16, padding: 56, textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎫</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>No tickets yet</div>
        </div>
      ) : (
        tickets.map(t => <TicketRow key={t.id} ticket={t} />)
      )}
    </div>
  );
}
